import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MdArticle,
  MdWork,
  MdDescription,
  MdMail,
  MdSchool,
  MdPeople,
  MdImage,
  MdBusiness,
} from 'react-icons/md';
import axiosInstance from '../../api/axiosInstance';

/* ─── helpers ─────────────────────────────────────────────── */
const safeCount = (result) => {
  // Promise.allSettled result — fulfilled → length of data array
  if (result.status !== 'fulfilled') return 0;
  const data = result.value?.data?.data;
  if (Array.isArray(data)) return data.length;
  return 0;
};

const safeItems = (result, n = 3) => {
  if (result.status !== 'fulfilled') return [];
  const data = result.value?.data?.data;
  if (Array.isArray(data)) return data.slice(0, n);
  return [];
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins || 1} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
};

/* ─── skeleton card ───────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100 animate-pulse">
    <div className="flex items-center justify-between">
      <div>
        <div className="h-3 w-20 sm:w-24 bg-gray-200 rounded mb-3" />
        <div className="h-7 sm:h-8 w-10 sm:w-12 bg-gray-200 rounded" />
      </div>
      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-200" />
    </div>
  </div>
);

/* ─── Dashboard ───────────────────────────────────────────── */
const Dashboard = () => {
  const [counts, setCounts] = useState(null); // null = loading
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);

  useEffect(() => {
    const load = async () => {
      // Fetch all in parallel — never let one failure break the rest
      const [
        blogsRes,
        careersRes,
        applicationsRes,
        contactsRes,
        admissionsRes,
        servicesRes,
        teamRes,
        galleryRes,
        faqRes,
      ] = await Promise.allSettled([
        axiosInstance.get('/blog'),
        axiosInstance.get('/career'),
        axiosInstance.get('/applications'),
        axiosInstance.get('/contacts'),
        axiosInstance.get('/admissions'),
        axiosInstance.get('/service'),
        axiosInstance.get('/team'),
        axiosInstance.get('/gallery'),
        axiosInstance.get('/faq'),
      ]);

      setCounts({
        blogs: safeCount(blogsRes),
        careers: safeCount(careersRes),
        applications: safeCount(applicationsRes),
        contacts: safeCount(contactsRes),
        admissions: safeCount(admissionsRes),
        services: safeCount(servicesRes),
        team: safeCount(teamRes),
        gallery: safeCount(galleryRes),
        faqs: safeCount(faqRes),
      });

      // Recent activity items
      setRecentBlogs(safeItems(blogsRes, 2));
      setRecentApplications(safeItems(applicationsRes, 2));
      setRecentContacts(safeItems(contactsRes, 2));
    };

    load();
  }, []);

  /* stat cards config — value filled from counts state */
  const statCards = counts
    ? [
        { label: 'Total Blogs', value: counts.blogs, icon: MdArticle, color: 'bg-blue-500', link: '/admin/blogs' },
        { label: 'Active Jobs', value: counts.careers, icon: MdWork, color: 'bg-green-500', link: '/admin/careers' },
        { label: 'Applications', value: counts.applications, icon: MdDescription, color: 'bg-purple-500', link: '/admin/applications' },
        { label: 'Messages', value: counts.contacts, icon: MdMail, color: 'bg-orange-500', link: '/admin/contacts' },
        { label: 'Admissions', value: counts.admissions, icon: MdSchool, color: 'bg-pink-500', link: '/admin/admissions' },
        { label: 'Services', value: counts.services, icon: MdBusiness, color: 'bg-indigo-500', link: '/admin/services' },
        { label: 'Team Members', value: counts.team, icon: MdPeople, color: 'bg-teal-500', link: '/admin/team' },
        { label: 'Gallery Images', value: counts.gallery, icon: MdImage, color: 'bg-cyan-500', link: '/admin/gallery' },
      ]
    : [];

  /* build a flat recent-activity feed */
  const recentActivity = [
    ...recentBlogs.map((b) => ({
      icon: MdArticle,
      label: `Blog: ${b.title || 'Untitled'}`,
      time: b.createdAt,
      color: 'text-blue-500',
    })),
    ...recentApplications.map((a) => ({
      icon: MdDescription,
      label: `Application from ${a.name || 'Unknown'}`,
      time: a.createdAt,
      color: 'text-purple-500',
    })),
    ...recentContacts.map((c) => ({
      icon: MdMail,
      label: `Message from ${c.name || 'Unknown'}`,
      time: c.createdAt,
      color: 'text-orange-500',
    })),
  ]
    .filter((item) => item.time)
    .sort((a, b) => new Date(b.time) - new Date(a.time))
    .slice(0, 6);

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard</h1>

      {/* ── Stats Grid ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
        {counts === null
          ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
          : statCards.map((stat) => (
              <Link
                key={stat.label}
                to={stat.link}
                className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100 hover:shadow-md transition-shadow block group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-xs sm:text-sm group-hover:text-[#008300] transition-colors">
                      {stat.label}
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-800 mt-2">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`${stat.color} p-3 sm:p-4 rounded-full text-white`}>
                    <stat.icon className="text-xl sm:text-2xl" />
                  </div>
                </div>
              </Link>
            ))}
      </div>

      {/* ── Quick Actions ───────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100 mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          {[
            { label: 'Add Blog', icon: MdArticle, to: '/admin/blogs/create' },
            { label: 'Add Career', icon: MdWork, to: '/admin/careers/create' },
            { label: 'Add Team', icon: MdPeople, to: '/admin/team/create' },
            { label: 'Upload Image', icon: MdImage, to: '/admin/gallery/create' },
          ].map((action) => (
            <Link
              key={action.label}
              to={action.to}
              className="p-3 sm:p-4 bg-[#008300]/10 rounded-lg hover:bg-[#008300]/20 transition-colors text-center flex flex-col items-center gap-2"
            >
              <action.icon className="text-xl sm:text-2xl text-[#008300]" />
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                {action.label}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* ── Recent Activity ─────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4">
          Recent Activity
        </h2>

        {counts === null && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-12 sm:h-14 bg-gray-100 rounded-lg animate-pulse"
              />
            ))}
          </div>
        )}

        {counts !== null && recentActivity.length === 0 && (
          <p className="text-gray-400 text-xs sm:text-sm text-center py-6">
            No recent activity found.
          </p>
        )}

        {counts !== null && recentActivity.length > 0 && (
          <div className="space-y-3">
            {recentActivity.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 sm:gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <item.icon className={`text-lg sm:text-xl flex-shrink-0 ${item.color}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-700 truncate">
                    {item.label}
                  </p>
                  <p className="text-xs text-gray-400">{timeAgo(item.time)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
